import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ShieldCheck, Link2Off, Check } from 'lucide-react';
import axios from 'axios';
import SecureLS from 'secure-ls';
import { setUser } from '../utils/secureStorage';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const FONT_H = "'Lora', Georgia, serif";
const FONT_B = "'Source Sans 3', sans-serif";

export default function RestoreAccount() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading | pending | cancelled | expired | restored
  const [data, setData] = useState(null);
  const [restoring, setRestoring] = useState(false);

  useEffect(() => {
    if (!token) { setStatus('expired'); return; }
    axios.get(`${API_BASE}/api/account/deletion/status/?restoreToken=${token}`)
      .then(res => {
        setData(res.data);
        setStatus(res.data.status === 'pending' ? 'pending' : res.data.status === 'cancelled' ? 'cancelled' : 'expired');
      })
      .catch(() => setStatus('expired'));
  }, [token]);

  const handleRestore = async () => {
    setRestoring(true);
    try {
      const res = await axios.post(`${API_BASE}/api/account/deletion/cancel/`, { restoreToken: token });
      if (res.data.token) {
        const ls = new SecureLS({ encodingType: 'aes' });
        ls.set('authToken', res.data.token);
        localStorage.setItem('isLoggedIn', 'true');
        if (res.data.user) {
          setUser(res.data.user);
        }
      }
      setStatus('restored');
    } catch (err) {
      const code = err.response?.data?.code;
      if (code === 'already_cancelled') setStatus('cancelled');
      else setStatus('expired');
    } finally {
      setRestoring(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#F5F5DC', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ background: '#fff', borderRadius: 20, border: '1px solid #CCC', maxWidth: 480, width: '100%', padding: '40px 36px', textAlign: 'center', boxShadow: '0 10px 30px rgba(0,0,0,.08)' }}>

        {status === 'loading' && (
          <>
            <div style={{ width: 48, height: 48, border: '4px solid #556B2F', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px' }} />
            <p style={{ fontSize: 17, color: '#556B2F', fontFamily: FONT_B }}>Checking your account...</p>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </>
        )}

        {status === 'pending' && (
          <>
            <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#FFF6E6', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <ShieldCheck size={32} color="#B5760A" />
            </div>
            <h1 style={{ fontFamily: FONT_H, fontWeight: 700, fontSize: 26, color: '#2C2F26', marginBottom: 8 }}>Your account is closing</h1>
            <p style={{ fontSize: 16, color: '#5A6150', fontFamily: FONT_B, marginBottom: 4 }}>
              Your data will be permanently deleted on <b>{data?.purgeAfter ? new Date(data.purgeAfter).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : 'soon'}</b>.
            </p>
            <p style={{ fontSize: 15, color: '#828A76', fontFamily: FONT_B, marginBottom: 24 }}>
              Changed your mind? Bring it back now.
            </p>
            <button onClick={handleRestore} disabled={restoring} style={{
              width: '100%', background: '#556B2F', color: '#fff', fontWeight: 700, fontSize: 17,
              padding: '16px', borderRadius: 12, border: 'none', cursor: 'pointer', fontFamily: FONT_B, minHeight: 56, marginBottom: 12,
            }}>
              {restoring ? 'Restoring...' : 'Bring my account back'}
            </button>
            <button onClick={() => window.location.href = '/login'} style={{
              width: '100%', background: 'transparent', color: '#828A76', fontWeight: 600, fontSize: 15,
              padding: '12px', borderRadius: 12, border: 'none', cursor: 'pointer', fontFamily: FONT_B,
            }}>
              Leave it closed
            </button>
          </>
        )}

        {status === 'restored' && (
          <>
            <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#EDF3E0', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <Check size={32} color="#556B2F" />
            </div>
            <h1 style={{ fontFamily: FONT_H, fontWeight: 700, fontSize: 26, color: '#2C2F26', marginBottom: 8 }}>Welcome back!</h1>
            <p style={{ fontSize: 16, color: '#5A6150', fontFamily: FONT_B, marginBottom: 24 }}>
              Your account has been restored. Everything is back to normal.
            </p>
            <button onClick={() => navigate('/my-life')} style={{
              width: '100%', background: '#556B2F', color: '#fff', fontWeight: 700, fontSize: 17,
              padding: '16px', borderRadius: 12, border: 'none', cursor: 'pointer', fontFamily: FONT_B, minHeight: 56,
            }}>
              Continue to My Life
            </button>
          </>
        )}

        {status === 'cancelled' && (
          <>
            <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#EDF3E0', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <Check size={32} color="#556B2F" />
            </div>
            <h1 style={{ fontFamily: FONT_H, fontWeight: 700, fontSize: 26, color: '#2C2F26', marginBottom: 8 }}>You've already brought this back</h1>
            <p style={{ fontSize: 16, color: '#5A6150', fontFamily: FONT_B, marginBottom: 24 }}>
              Your account is active. Sign in to continue.
            </p>
            <button onClick={() => navigate('/login')} style={{
              width: '100%', background: '#556B2F', color: '#fff', fontWeight: 700, fontSize: 17,
              padding: '16px', borderRadius: 12, border: 'none', cursor: 'pointer', fontFamily: FONT_B, minHeight: 56,
            }}>
              Sign in
            </button>
          </>
        )}

        {status === 'expired' && (
          <>
            <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#FFF6E6', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <Link2Off size={32} color="#B5760A" />
            </div>
            <h1 style={{ fontFamily: FONT_H, fontWeight: 700, fontSize: 26, color: '#2C2F26', marginBottom: 8 }}>This link has expired</h1>
            <p style={{ fontSize: 16, color: '#5A6150', fontFamily: FONT_B, marginBottom: 24 }}>
              Your account was closed and can't be brought back.
            </p>
            <button onClick={() => navigate('/auth/register')} style={{
              width: '100%', background: '#556B2F', color: '#fff', fontWeight: 700, fontSize: 17,
              padding: '16px', borderRadius: 12, border: 'none', cursor: 'pointer', fontFamily: FONT_B, minHeight: 56,
            }}>
              Create a new account
            </button>
          </>
        )}
      </div>
    </div>
  );
}
